import { AUTH_ROUTE_PATHS } from "./constants";
import type { AuthSearch, AuthSearchValue, ResolveAuthHrefOptions } from "./types";

const appendSearchValue = (searchParams: URLSearchParams, key: string, value: AuthSearchValue): void => {
  if (value !== null && value !== undefined) {
    searchParams.append(key, String(value));
  }
};

const createSearchParams = (search?: AuthSearch): URLSearchParams => {
  if (search instanceof URLSearchParams) {
    return new URLSearchParams(search);
  }

  const searchParams = new URLSearchParams();

  Object.entries(search ?? {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => appendSearchValue(searchParams, key, item));
      return;
    }

    appendSearchValue(searchParams, key, value as AuthSearchValue);
  });

  return searchParams;
};

const splitAuthBase = (authBase: string): { pathname: string; searchParams: URLSearchParams } => {
  const queryIndex = authBase.indexOf("?");
  const pathname = queryIndex === -1 ? authBase : authBase.slice(0, queryIndex);
  const query = queryIndex === -1 ? "" : authBase.slice(queryIndex + 1);
  const isAbsoluteRoot = /^[a-z][a-z\d+.-]*:\/\/[^/]+\/+$/i.test(pathname);
  const normalizedPathname = isAbsoluteRoot ? pathname.replace(/\/+$/, "/") : pathname.replace(/\/+$/, "") || "/";

  return {
    pathname: normalizedPathname,
    searchParams: new URLSearchParams(query),
  };
};

const appendSearch = (href: string, searchParams: URLSearchParams): string => {
  const query = searchParams.toString();

  return query ? `${href}?${query}` : href;
};

const mergeSearch = (href: string, overrides: URLSearchParams): string => {
  if (overrides.size === 0) {
    return href;
  }

  const queryIndex = href.indexOf("?");
  const pathname = queryIndex === -1 ? href : href.slice(0, queryIndex);
  const searchParams = new URLSearchParams(queryIndex === -1 ? "" : href.slice(queryIndex + 1));

  overrideSearchParams(searchParams, overrides);

  return appendSearch(pathname, searchParams);
};

const mergeSearchIntoPredefinedHref = (href: string, searchParams: URLSearchParams): string => {
  const hashIndex = href.indexOf("#");

  if (hashIndex === -1) {
    return mergeSearch(href, searchParams);
  }

  const documentHref = href.slice(0, hashIndex);
  const fragmentHref = href.slice(hashIndex + 1);

  if (fragmentHref.startsWith("/")) {
    return `${documentHref}#${mergeSearch(fragmentHref, searchParams)}`;
  }

  return `${mergeSearch(documentHref, searchParams)}#${fragmentHref}`;
};

const overrideSearchParams = (target: URLSearchParams, overrides: URLSearchParams): void => {
  new Set(overrides.keys()).forEach((key) => {
    const [firstValue, ...additionalValues] = overrides.getAll(key);

    target.set(key, firstValue);
    additionalValues.forEach((value) => target.append(key, value));
  });
};

export const mergeAuthSearchParams = (documentSearch: string, hash: string): URLSearchParams => {
  const searchParams = new URLSearchParams(documentSearch);
  const hashQueryIndex = hash.indexOf("?");

  if (hash.startsWith("#/") && hashQueryIndex !== -1) {
    const hashSearchParams = new URLSearchParams(hash.slice(hashQueryIndex + 1));
    overrideSearchParams(searchParams, hashSearchParams);
  }

  return searchParams;
};

/**
 * Resolves an authentication href from the supplied routing options.
 *
 * Legacy content can provide `predefinedHref` to use an authored URL instead
 * of generating one. Caller-provided search parameters are merged into it.
 */
export const resolveAuthHref = (options: ResolveAuthHrefOptions): string => {
  const routeSearchParams = createSearchParams(options.search);

  if (options.predefinedHref !== undefined) {
    return mergeSearchIntoPredefinedHref(options.predefinedHref, routeSearchParams);
  }

  const { authBasePath, authPageUrl, route, isAppRouterEnabled, pathSegments = [] } = options;
  const { pathname, searchParams: baseSearchParams } = splitAuthBase(authBasePath ?? authPageUrl);
  const resolvedRoute = `${route}${pathSegments.map((segment) => `/${encodeURIComponent(segment)}`).join("")}`;

  if (route === AUTH_ROUTE_PATHS.entry) {
    overrideSearchParams(baseSearchParams, routeSearchParams);
    return appendSearch(pathname, baseSearchParams);
  }

  if (isAppRouterEnabled) {
    overrideSearchParams(baseSearchParams, routeSearchParams);
    return appendSearch(`${pathname.replace(/\/$/, "")}${resolvedRoute}`, baseSearchParams);
  }

  const authBaseHref = appendSearch(pathname, baseSearchParams);
  return appendSearch(`${authBaseHref}#${resolvedRoute}`, routeSearchParams);
};
